import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { CustomersTable } from "~/components/child/customers-table";
import { ReferralLinkCard } from "~/components/child/referral-link-card";
import { ChildAcquisitionPartnerSummaryTable } from "~/components/parent/child-acquisition-partner-summary-table";
import { Page, PageHeader, Section } from "~/components/shell/page";
import { Button } from "~/components/ui/button";
import { Dash } from "~/components/ui/dash";
import { Stat, StatGrid } from "~/components/ui/stat";
import { Note } from "~/components/ui/typography";
import { customersQueryOptions } from "~/lib/api/customers";
import { isParent, meQueryOptions } from "~/lib/api/me";
import { childAcquisitionPartnersQueryOptions } from "~/lib/api/partners";
import { formatNumber } from "~/lib/format";

const PREVIEW_COUNT = 5;

// Placeholder figures until the parent dashboard metrics exist (#2128).
const placeholderMetrics = {
  customersAcquired: 0,
  mandatesSigned: 0,
  chargersLinked: 0,
};

export const Route = createFileRoute("/_authenticated/overzicht")({
  loader: async ({ context }) => {
    if (isParent(context.partner)) {
      await context.queryClient.query(childAcquisitionPartnersQueryOptions());
    } else {
      await context.queryClient.query(customersQueryOptions());
    }
  },
  component: OverviewPage,
});

function OverviewPage() {
  const { data: partner } = useSuspenseQuery(meQueryOptions());

  return isParent(partner) ? (
    <ParentAcquisitionPartnerOverviewPage />
  ) : (
    <ChildAcquisitionPartnerOverviewPage />
  );
}

function shareOfAcquired(count: number): string {
  const { customersAcquired } = placeholderMetrics;
  if (customersAcquired === 0) return "— van aangebracht";
  return `${Math.round((count / customersAcquired) * 100)}% van aangebracht`;
}

function ParentAcquisitionPartnerOverviewPage() {
  const { data: childPartners } = useSuspenseQuery(
    childAcquisitionPartnersQueryOptions(),
  );
  const total = childPartners.length;

  return (
    <Page>
      <PageHeader title="Overzicht">
        Alles wat via jouw partners bij Lekkerladen is aangebracht, opgeteld over dit
        kalenderjaar. Cijfers zijn geaggregeerd: er zijn geen gegevens van individuele
        eindklanten zichtbaar.
      </PageHeader>

      <StatGrid>
        <Stat label="Actieve partners" value={formatNumber(total)} />
        <Stat
          label="Klanten aangebracht"
          value={formatNumber(placeholderMetrics.customersAcquired)}
        />
        <Stat
          label="Machtiging getekend"
          value={formatNumber(placeholderMetrics.mandatesSigned)}
          foot={shareOfAcquired(placeholderMetrics.mandatesSigned)}
        />
        <Stat
          label="Laadpaal gekoppeld"
          value={formatNumber(placeholderMetrics.chargersLinked)}
          foot={shareOfAcquired(placeholderMetrics.chargersLinked)}
        />
        <Stat
          label="Gem. contractduur getekend"
          value={<Dash />}
          foot="gemiddelde looptijd machtiging"
        />
      </StatGrid>

      <Section
        title="Partners"
        description={
          total > PREVIEW_COUNT
            ? `De eerste ${PREVIEW_COUNT} van de ${formatNumber(total)} partners.`
            : undefined
        }
        action={
          <Button asChild>
            <Link to="/partners">
              Alle partners
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        }
      >
        <ChildAcquisitionPartnerSummaryTable
          childPartners={childPartners.slice(0, PREVIEW_COUNT)}
          total={total}
        />
      </Section>
    </Page>
  );
}

function ChildAcquisitionPartnerOverviewPage() {
  const { data: partner } = useSuspenseQuery(meQueryOptions());
  const { data: customers } = useSuspenseQuery(customersQueryOptions());
  const total = customers.length;
  const year = new Date().getFullYear();

  return (
    <Page>
      <PageHeader title="Overzicht">
        De klanten die jij hebt aangebracht bij Lekkerladen en waar je in de keten van
        staat.
      </PageHeader>

      <ReferralLinkCard referralCode={partner.referralCode ?? ""} />

      <section className="flex flex-col gap-4">
        {/* Only the customer count is real; the rest is pending the child dashboard metrics (#2128). */}
        <StatGrid>
          <Stat label="Klanten aangebracht" value={formatNumber(total)} />
          <Stat label="Machtiging getekend" value={<Dash />} foot="— van aangebracht" />
          <Stat label="Laadpaal gekoppeld" value={<Dash />} foot="— van aangebracht" />
          <Stat label="Geconverteerd" value={<Dash />} foot="— nog niet geconverteerd" />
          <Stat
            inverse
            label={`Verwacht aandeel ${year}`}
            value={<Dash className="text-fg-inverse-muted" />}
            foot={`zelffactuur na audit van ${year}`}
          />
        </StatGrid>
        <Note>
          Een klant telt als geconverteerd zodra de machtiging 14 dagen geleden is
          getekend en er minstens één laadsessie op het account staat. Je verdient een
          aandeel per verhandelbare kWh van je geconverteerde klanten: alleen kWh die
          slagen voor onze controles en als ERE verhandeld kunnen worden tellen mee. Het
          aandeel is een deel van de commissie van Lekkerladen, berekend op de
          gerealiseerde prijs en niet op de marktprijs. Cijfers tijdens het jaar zijn een
          verwachting; sessies die de audit niet doorstaan tellen niet mee. Na afloop van
          het jaar, zodra alle sessies zijn gevalideerd en door onze auditor
          gecontroleerd, sturen wij je een zelffactuur met je aandeel over het hele jaar.
        </Note>
      </section>

      <Section
        title="Recent aangebrachte klanten"
        description={
          total > PREVIEW_COUNT
            ? `De ${PREVIEW_COUNT} nieuwste van de ${formatNumber(total)} klanten. Je wervingspartner ziet deze lijst niet.`
            : "Alleen de klanten die jij hebt aangebracht. Je wervingspartner ziet deze lijst niet."
        }
        action={
          <Button asChild>
            <Link to="/klanten">
              Alle klanten
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        }
      >
        <CustomersTable customers={customers.slice(0, PREVIEW_COUNT)} />
      </Section>
    </Page>
  );
}
